/**
 * demoFixtures.ts — In-Memory-Mock-Daten für `/case/demo/cockpit` und
 * verwandte Demo-Routes.
 *
 * Phase B-Konsolidierung: damit die Berater-Sicht im Demo-Modus alle
 * Phase-A-Sektionen (Stammdaten, Multi-Doc, Rückfragen) zeigt, statt sie
 * hinter dem `isLiveCase`-Gate verschwinden zu lassen.
 *
 * Wichtig: Diese Daten haben KEINE Side-Effects — sie sind nur für die UI-
 * Vorschau. Edits/Uploads im Demo-Modus sind no-ops mit kurzer Toast-
 * Meldung im Aufrufer.
 *
 * Wir benutzen plausible Mustermann-Daten, die zur bestehenden Demo-
 * Stellungnahme (`buildDemoStatementInput`) passen.
 */

import type {
  CaseClarificationRow,
  CaseDocumentRow,
  CaseRow,
  ConsentRecordRow,
  DsgvoRequestRow,
} from '../supabaseTypes';

const DEMO_CASE_ID = 'demo' as const;

const NOW_ISO = '2026-05-14T10:00:00Z';
const TWO_DAYS_AGO_ISO = '2026-05-12T08:30:00Z';
const FIVE_DAYS_AGO_ISO = '2026-05-09T14:15:00Z';
const SEVEN_DAYS_AGO_ISO = '2026-05-07T11:45:00Z';

/** Demo-Case mit vollständigen Stammdaten — passend zur Demo-Stellungnahme. */
export const DEMO_CASE_ROW: CaseRow = {
  id: DEMO_CASE_ID,
  owner_id: 'demo-user',
  tenant_id: null,
  type: 'b2g',
  legal_basis: 'par10',
  status: 'in_progress',
  client_full_name: 'Maria Mustermann',
  client_birthdate: '1980-05-14',
  client_address: 'Hauptstraße 12\n80331 München',
  jobcenter_name: 'Demo-Jobcenter (Stadt)',
  jc_address_line1: null,
  jc_address_line2: null,
  jc_contact_name: null,
  jc_file_number: 'IFK-2026-1234',
  order_date: null,
  data_submitted_at: null,
  meeting_date: null,
  meeting_duration_min: null,
  meeting_format: null,
  benefit_start_date: null,
  additional_docs: null,
  coach_name: null,
  business_branch: 'Beratung',
  business_legal_form: 'Einzelunternehmen',
  business_start_date: '2024-04-01',
  business_tax_id: '21/123/45678',
  business_description:
    'Buchhaltung und Lohnabrechnung für kleine Handwerksbetriebe in München. Pauschalpreis pro Mandat (60–200 Euro im Monat).',
  created_at: SEVEN_DAYS_AGO_ISO,
  updated_at: NOW_ISO,
  completed_at: null,
  deleted_at: null,
};

/**
 * Demo-Dokumente: drei Stück, einer pro Doc-Typ-Kategorie.
 * UUIDs sind synthetisch — sie sind im Demo-Modus nie echte Storage-Objekte.
 */
export const DEMO_DOCUMENTS: ReadonlyArray<CaseDocumentRow> = [
  {
    id: '00000000-0000-0000-0000-000000000001',
    case_id: DEMO_CASE_ID,
    doc_type: 'bwa',
    storage_path: 'demo/bwa/Mustermann_BWA_Q1_2026.xlsx',
    original_filename: 'Mustermann_BWA_Q1_2026.xlsx',
    mime_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    size_bytes: 14_532,
    uploaded_by: 'demo-mandant',
    uploaded_by_role: 'mandant',
    uploaded_at: FIVE_DAYS_AGO_ISO,
    replaced_by: null,
    description: null,
    deleted_at: null,
    created_at: FIVE_DAYS_AGO_ISO,
  },
  {
    id: '00000000-0000-0000-0000-000000000002',
    case_id: DEMO_CASE_ID,
    doc_type: 'mietvertrag',
    storage_path: 'demo/mietvertrag/Mietvertrag_2024.pdf',
    original_filename: 'Mietvertrag_2024.pdf',
    mime_type: 'application/pdf',
    size_bytes: 412_018,
    uploaded_by: 'demo-mandant',
    uploaded_by_role: 'mandant',
    uploaded_at: TWO_DAYS_AGO_ISO,
    replaced_by: null,
    description: null,
    deleted_at: null,
    created_at: TWO_DAYS_AGO_ISO,
  },
  {
    id: '00000000-0000-0000-0000-000000000003',
    case_id: DEMO_CASE_ID,
    doc_type: 'sonstiges',
    storage_path: 'demo/sonstiges/Auftragsbestand_Mai_2026.pdf',
    original_filename: 'Auftragsbestand_Mai_2026.pdf',
    mime_type: 'application/pdf',
    size_bytes: 89_120,
    uploaded_by: 'demo-mandant',
    uploaded_by_role: 'mandant',
    uploaded_at: NOW_ISO,
    replaced_by: null,
    description: null,
    deleted_at: null,
    created_at: NOW_ISO,
  },
];

/**
 * Demo-Rückfragen: zwei offene + eine gelöste, damit die drei Tabs in
 * `<ClarificationsSection>` jeweils Inhalt haben.
 */
export const DEMO_CLARIFICATIONS: ReadonlyArray<CaseClarificationRow> = [
  // Offene Rückfrage des Beraters — Mandant hat noch nicht geantwortet
  {
    id: '00000000-1111-0000-0000-000000000001',
    case_id: DEMO_CASE_ID,
    field_path: 'resources.bwa.revenue',
    field_label: 'Monatlicher Umsatz (netto)',
    created_by: 'demo-berater',
    created_by_role: 'berater',
    question:
      'Bitte BWA für Q1 2026 nachreichen — die hochgeladene Datei betrifft nur Q4 2025. Ist das ein Versehen?',
    answer: null,
    answered_by: null,
    answered_at: null,
    resolved_at: null,
    resolved_by: null,
    created_at: TWO_DAYS_AGO_ISO,
    updated_at: TWO_DAYS_AGO_ISO,
  },
  // Offene Rückfrage des Mandanten — Berater muss antworten
  {
    id: '00000000-1111-0000-0000-000000000002',
    case_id: DEMO_CASE_ID,
    field_path: 'resources.bg.kdu',
    field_label: 'Kosten der Unterkunft (KdU)',
    created_by: 'demo-mandant',
    created_by_role: 'mandant',
    question:
      'Mein Vermieter hat zum 1. Mai die Kaltmiete um 30 Euro erhöht. Soll ich den neuen oder alten Wert eintragen?',
    answer: null,
    answered_by: null,
    answered_at: null,
    resolved_at: null,
    resolved_by: null,
    created_at: FIVE_DAYS_AGO_ISO,
    updated_at: FIVE_DAYS_AGO_ISO,
  },
  // Bereits gelöst — für den „Verlauf"-Tab
  {
    id: '00000000-1111-0000-0000-000000000003',
    case_id: DEMO_CASE_ID,
    field_path: 'stammdaten.taxId',
    field_label: 'Steuernummer (Finanzamt)',
    created_by: 'demo-mandant',
    created_by_role: 'mandant',
    question: 'Brauchen Sie die Umsatzsteuer-ID oder die normale Steuernummer?',
    answer:
      'Die normale Steuernummer Ihres Finanzamts (z.B. 21/123/45678). Die persönliche Steuer-ID brauchen wir nicht.',
    answered_by: 'demo-berater',
    answered_at: SEVEN_DAYS_AGO_ISO,
    resolved_at: SEVEN_DAYS_AGO_ISO,
    resolved_by: 'demo-berater',
    created_at: SEVEN_DAYS_AGO_ISO,
    updated_at: SEVEN_DAYS_AGO_ISO,
  },
];

/** True wenn die übergebene caseId die Demo-Sentinel ist. */
export function isDemoCaseId(caseId: string): boolean {
  return caseId === DEMO_CASE_ID;
}

/**
 * Demo-Einwilligungen für die Mandanten-DSGVO-Self-Service-Page.
 * Eine aktive (Tragfähigkeitsprüfung), eine bereits widerrufene
 * (Übermittlung) — damit beide UI-Zweige sichtbar sind.
 */
export const DEMO_CONSENT_RECORDS: ReadonlyArray<ConsentRecordRow> = [
  {
    id: '00000000-2222-0000-0000-000000000001',
    user_id: 'demo-mandant',
    case_id: DEMO_CASE_ID,
    purpose: 'tragfaehigkeitspruefung',
    granted: true,
    granted_at: SEVEN_DAYS_AGO_ISO,
    withdrawn_at: null,
    withdrawal_reason: null,
    policy_version: '1.0',
    policy_url: null,
    policy_text_hash: null,
    ip_address: null,
    user_agent: null,
    created_at: SEVEN_DAYS_AGO_ISO,
    updated_at: SEVEN_DAYS_AGO_ISO,
    deleted_at: null,
  },
  {
    id: '00000000-2222-0000-0000-000000000002',
    user_id: 'demo-mandant',
    case_id: DEMO_CASE_ID,
    purpose: 'jobcenter_uebermittlung',
    granted: true,
    granted_at: SEVEN_DAYS_AGO_ISO,
    withdrawn_at: TWO_DAYS_AGO_ISO,
    withdrawal_reason: 'Demo: Mandant hat Übermittlung widerrufen.',
    policy_version: '1.0',
    policy_url: null,
    policy_text_hash: null,
    ip_address: null,
    user_agent: null,
    created_at: SEVEN_DAYS_AGO_ISO,
    updated_at: TWO_DAYS_AGO_ISO,
    deleted_at: null,
  },
];

/**
 * Demo-DSGVO-Anfragen für die Mandanten- und Berater-Sicht.
 * Eine offene (Datenexport, sehr nahe Frist), eine in Bearbeitung (Löschung),
 * eine erfüllte (Datenexport) — damit alle Tabs Inhalt haben.
 */
export const DEMO_DSGVO_REQUESTS: ReadonlyArray<DsgvoRequestRow> = [
  {
    id: '00000000-3333-0000-0000-000000000001',
    case_id: DEMO_CASE_ID,
    request_type: 'data_export',
    requested_by: 'demo-mandant',
    requested_by_role: 'mandant',
    requested_at: TWO_DAYS_AGO_ISO,
    reason: 'Ich möchte einen vollständigen Überblick meiner Daten.',
    status: 'open',
    handled_by: null,
    handled_at: null,
    response_text: null,
    response_artifact_path: null,
    // Frist in 28 Tagen relativ zur „Demo-Now"
    legal_deadline_at: '2026-06-09T08:30:00Z',
    created_at: TWO_DAYS_AGO_ISO,
    updated_at: TWO_DAYS_AGO_ISO,
  },
  {
    id: '00000000-3333-0000-0000-000000000002',
    case_id: DEMO_CASE_ID,
    request_type: 'deletion',
    requested_by: 'demo-mandant',
    requested_by_role: 'mandant',
    requested_at: FIVE_DAYS_AGO_ISO,
    reason: null,
    status: 'in_progress',
    handled_by: null,
    handled_at: null,
    response_text: null,
    response_artifact_path: null,
    // Frist in 5 Tagen — urgent
    legal_deadline_at: '2026-05-19T14:15:00Z',
    created_at: FIVE_DAYS_AGO_ISO,
    updated_at: FIVE_DAYS_AGO_ISO,
  },
  {
    id: '00000000-3333-0000-0000-000000000003',
    case_id: DEMO_CASE_ID,
    request_type: 'data_export',
    requested_by: 'demo-mandant',
    requested_by_role: 'mandant',
    requested_at: SEVEN_DAYS_AGO_ISO,
    reason: null,
    status: 'fulfilled',
    handled_by: 'demo-berater',
    handled_at: FIVE_DAYS_AGO_ISO,
    response_text: 'Datenexport wurde am 09.05.2026 per verschlüsselter E-Mail zugestellt.',
    response_artifact_path: null,
    legal_deadline_at: '2026-06-06T11:45:00Z',
    created_at: SEVEN_DAYS_AGO_ISO,
    updated_at: FIVE_DAYS_AGO_ISO,
  },
];
